import {
  Employee,
  EmployeeDashboardStats,
  EmployeeFilters,
  SearchResult,
  DepartmentStats,
  CountryStats
} from '../../types/employee-directory';
import { firestoreWrapper } from '../firestoreWrapper';
import { auth } from '../../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import axios from 'axios';
import { DocumentData } from 'firebase/firestore';

// Firebase Functions base URL
const FIREBASE_FUNCTIONS_BASE_URL = 'https://us-central1-jls-apps.cloudfunctions.net';

// Initialize Firebase Functions
const functions = getFunctions();

// Helper function to get Firebase Function URL
const getFirebaseFunctionUrl = (functionName: string): string => {
  return `${FIREBASE_FUNCTIONS_BASE_URL}/${functionName}`;
};

// Create axios instance with configuration
const api = axios.create({
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use(async (config: any) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return config;
  }
});

// Cache configuration
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const EMPLOYEE_CACHE_KEY = 'employeeDirectoryCache';

// PHASE 1 OPTIMIZATION: Request Deduplication
// Prevents multiple simultaneous identical requests
class RequestDeduplicator {
  private pendingRequests = new Map<string, Promise<any>>();

  async deduplicate<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    // Check if request is already pending
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Start new request
    const promise = fetcher();
    this.pendingRequests.set(key, promise);

    // Clean up when request completes (success or failure)
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });

    return promise;
  }

  // Get current pending requests for debugging
  getPendingRequests(): string[] {
    return Array.from(this.pendingRequests.keys());
  }

  // Clear all pending requests (for testing/debugging)
  clearAll(): void {
    this.pendingRequests.clear();
  }
}

// Initialize request deduplicator
const requestDeduplicator = new RequestDeduplicator();

// PERFORMANCE IMPROVEMENT: Direct Firestore SDK Methods
// These methods bypass Firebase Functions for dramatically better performance
const firestoreDirectService = {
  // Direct Firestore method to get all employees
  async getAllEmployeesDirect(): Promise<Employee[]> {
    try {
      // Query the employees collection directly
      const employeesRef = firestoreWrapper.collection('employees');
      const querySnapshot = await firestoreWrapper.getDocs(employeesRef);
      
      const employees: Employee[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as DocumentData;
        employees.push({
          id: doc.id,
          ...data
        } as Employee);
      });

      return employees;
    } catch (error) {
      console.error('Direct Firestore query failed:', error);
      throw error;
    }
  },

  // Direct Firestore method to get dashboard data
  async getDashboardCompleteDirect(): Promise<any> {
    try {
      // Get all employees directly
      const employees = await this.getAllEmployeesDirect();
      
      // Calculate stats locally (much faster than server-side processing)
      const stats = this.calculateDashboardStats(employees);
      
      const result = {
        ...stats,
        employees,
        performance: {
          source: 'direct_firestore',
          employeeCount: employees.length
        }
      };
      return result;
    } catch (error) {
      console.error('Direct dashboard calculation failed:', error);
      throw error;
    }
  },

  // Local calculation of dashboard stats (avoiding server-side processing)
  calculateDashboardStats(employees: Employee[]) {
    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    
    // Calculate basic stats
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(emp => emp.status === 'active').length;
    const newHiresThisMonth = employees.filter(emp =>
      emp.startDate && new Date(emp.startDate) >= oneMonthAgo
    ).length;

    // Department breakdown
    const departmentCounts: Record<string, number> = {};
    employees.forEach(emp => {
      if (emp.department) {
        departmentCounts[emp.department] = (departmentCounts[emp.department] || 0) + 1;
      }
    });

    const departmentBreakdown = Object.entries(departmentCounts).map(([department, count]) => ({
      department,
      count,
      percentage: (count / totalEmployees) * 100
    }));

    // Country breakdown
    const countryCounts: Record<string, number> = {};
    employees.forEach(emp => {
      if (emp.country) {
        countryCounts[emp.country] = (countryCounts[emp.country] || 0) + 1;
      }
    });

    const countryBreakdown = Object.entries(countryCounts).map(([country, count]) => ({
      country,
      count,
      percentage: (count / totalEmployees) * 100
    }));

    // Calculate 30-day window for filtering
    const thirtyDaysFromNow = new Date(now);
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    // Filter for employees with birthdays in next 30 days
    const employeesWithUpcomingBirthdays = employees.filter(emp => {
      if (!emp.birthday) return false;
      
      const birthdayDate = new Date(emp.birthday);
      const birthdayThisYear = new Date(now.getFullYear(), birthdayDate.getMonth(), birthdayDate.getDate());
      
      // If birthday has passed this year, check next year
      if (birthdayThisYear < now) {
        birthdayThisYear.setFullYear(now.getFullYear() + 1);
      }
      
      return birthdayThisYear <= thirtyDaysFromNow;
    });

    // Filter for employees with anniversaries in next 30 days
    const employeesWithUpcomingAnniversaries = employees.filter(emp => {
      if (!emp.startDate) return false;
      
      const startDate = new Date(emp.startDate);
      const anniversaryThisYear = new Date(now.getFullYear(), startDate.getMonth(), startDate.getDate());
      
      // If anniversary has passed this year, check next year
      if (anniversaryThisYear < now) {
        anniversaryThisYear.setFullYear(now.getFullYear() + 1);
      }
      
      return anniversaryThisYear <= thirtyDaysFromNow;
    });
    
    // Create birthday objects with both flattened properties and employee object
    const upcomingBirthdays = employeesWithUpcomingBirthdays.map((emp: Employee) => {
      // Calculate this year's birthday with null check
      if (!emp.birthday) {
        return {
          firstName: emp.firstName,
          lastName: emp.lastName,
          upcomingBirthday: new Date().toISOString(),
          employee: emp
        };
      }
      
      const birthdayDate = new Date(emp.birthday);
      const birthdayThisYear = new Date(now.getFullYear(), birthdayDate.getMonth(), birthdayDate.getDate());
      
      // If birthday has passed this year, show next year's birthday
      if (birthdayThisYear < now) {
        birthdayThisYear.setFullYear(now.getFullYear() + 1);
      }
      
      return {
        firstName: emp.firstName,
        lastName: emp.lastName,
        upcomingBirthday: birthdayThisYear.toISOString(),
        employee: emp
      };
    }).sort((a, b) => new Date(a.upcomingBirthday).getTime() - new Date(b.upcomingBirthday).getTime());
    
    // Create anniversary objects with both flattened properties and employee object
    const upcomingAnniversaries = employeesWithUpcomingAnniversaries.map((emp: Employee) => {
      // Calculate this year's anniversary and years of service with null check
      if (!emp.startDate) {
        return {
          firstName: emp.firstName,
          lastName: emp.lastName,
          anniversaryDate: new Date().toISOString(),
          yearsOfService: 0,
          employee: emp
        };
      }
      
      const startDate = new Date(emp.startDate);
      const anniversaryThisYear = new Date(now.getFullYear(), startDate.getMonth(), startDate.getDate());
      const yearsOfService = now.getFullYear() - startDate.getFullYear();
      
      // If anniversary has passed this year, show next year's anniversary
      if (anniversaryThisYear < now) {
        anniversaryThisYear.setFullYear(now.getFullYear() + 1);
      }
      
      return {
        firstName: emp.firstName,
        lastName: emp.lastName,
        anniversaryDate: anniversaryThisYear.toISOString(),
        yearsOfService: yearsOfService,
        employee: emp
      };
    }).sort((a, b) => new Date(a.anniversaryDate).getTime() - new Date(b.anniversaryDate).getTime());

    const finalStats = {
      stats: {
        totalEmployees,
        totalDepartments: Object.keys(departmentCounts).length,
        totalCountries: Object.keys(countryCounts).length,
        totalCities: new Set(employees.map(emp => emp.city).filter(Boolean)).size,
        activeEmployees,
        newHiresThisMonth
      },
      departmentBreakdown,
      countryBreakdown,
      upcomingBirthdays,
      upcomingAnniversaries,
      allBirthdays: upcomingBirthdays,
      allAnniversaries: upcomingAnniversaries
    };
    return finalStats;
  }
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Load cache from localStorage
const loadCache = <T>(key: string): CacheEntry<T> | null => {
  const cached = localStorage.getItem(key);
  if (cached) {
    try {
      return JSON.parse(cached);
    } catch (e) {
      console.error('Error parsing employee directory cache:', e);
      return null;
    }
  }
  return null;
};

// Save cache to localStorage
const saveCache = <T>(key: string, data: T) => {
  try {
    const cacheData: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cacheData));
  } catch (e) {
    console.error('Error saving employee directory cache:', e);
  }
};

// Clear cache
export const clearEmployeeDirectoryCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(EMPLOYEE_CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.error('Error clearing employee directory cache:', e);
  }
};

interface EmployeeDirectoryAdmin {
  email: string;
  firstName?: string;
  lastName?: string;
  addedAt: string;
  isActive: boolean;
}

interface EmployeeDirectoryService {
  // PHASE 1 OPTIMIZATION: Unified dashboard method
  getDashboardComplete(): Promise<any>;
  getAllEmployees(): Promise<Employee[]>;
  getEmployee(id: string): Promise<Employee | null>;
  searchEmployees(query: string, filters?: EmployeeFilters): Promise<SearchResult>;
  getEmployeesByDepartment(department: string): Promise<Employee[]>;
  getEmployeesByLocation(location: string): Promise<Employee[]>;
  getDashboardMetrics(): Promise<EmployeeDashboardStats>;
  getDepartmentStats(): Promise<DepartmentStats[]>;
  getLocationStats(): Promise<CountryStats[]>;
  updateEmployee(employee: Employee): Promise<void>;
  addEmployee(employee: Omit<Employee, 'id'>): Promise<string>;
  deleteEmployee(id: string): Promise<void>;
  uploadEmployeePhoto(id: string, photoFile: File): Promise<string>;
  deleteEmployeePhoto(id: string): Promise<void>;
  getFilterOptions(): Promise<{
    departments: string[];
    locations: string[];
    jobTitles: string[];
    skills: string[];
  }>;
  // Admin functions
  getAdmins(): Promise<EmployeeDirectoryAdmin[]>;
  addAdmin(email: string, firstName?: string, lastName?: string): Promise<void>;
  removeAdmin(email: string): Promise<void>;
  checkUserAuth(email: string): Promise<{ isAuthorized: boolean; isAdmin: boolean }>;
  manualSync(): Promise<{ success: boolean; message: string; stats?: any }>;
}

// Create and export the employeeDirectoryService
export const employeeDirectoryService: EmployeeDirectoryService = {
  // PERFORMANCE OPTIMIZATION: Direct Firestore Dashboard Method
  // Bypasses Firebase Functions for dramatically improved performance
  async getDashboardComplete(): Promise<any> {
    const cacheKey = `${EMPLOYEE_CACHE_KEY}_dashboard_complete_direct_v4`; // v4 to force cache refresh after adding date sorting
    
    return requestDeduplicator.deduplicate(cacheKey, async () => {
      const cached = loadCache<any>(cacheKey);
      const now = Date.now();

      // Return cached data if fresh
      if (cached && now - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      try {
        // PERFORMANCE IMPROVEMENT: Use direct Firestore access instead of Firebase Functions
        const completeData = await firestoreDirectService.getDashboardCompleteDirect();
        
        // Save to cache
        saveCache(cacheKey, completeData);
        return completeData;
      } catch (error: any) {
        console.error('Error fetching complete dashboard data via direct Firestore:', error);
        
        // FALLBACK: Try Firebase Functions if direct Firestore fails
        console.warn('Falling back to Firebase Functions due to direct Firestore error');
        try {
          const response = await api.get(getFirebaseFunctionUrl('getEmployeeDashboardComplete'));
          const completeData = response.data.data || response.data;
          saveCache(cacheKey, completeData);
          return completeData;
        } catch (fallbackError) {
          console.error('Fallback Firebase Function also failed:', fallbackError);
        }
        
        // Return cached data if available, even if stale
        if (cached) {
          console.warn('Using stale dashboard cache due to all methods failing');
          return cached.data;
        }
        
        throw error;
      }
    });
  },

  async getAllEmployees(): Promise<Employee[]> {
    const cacheKey = `${EMPLOYEE_CACHE_KEY}_all_employees_direct`;
    
    return requestDeduplicator.deduplicate(cacheKey, async () => {
      const cached = loadCache<Employee[]>(cacheKey);
      const now = Date.now();

      // Return cached data if fresh
      if (cached && now - cached.timestamp < CACHE_TTL) {
        return cached.data;
      }

      try {
        // PERFORMANCE IMPROVEMENT: Use direct Firestore access instead of Firebase Functions
        const employees = await firestoreDirectService.getAllEmployeesDirect();
        
        // Save to cache
        saveCache(cacheKey, employees);
        return employees;
      } catch (error: any) {
        console.error('Error fetching employees via direct Firestore:', error);
        
        // FALLBACK: Try Firebase Functions if direct Firestore fails
        console.warn('Falling back to Firebase Functions due to direct Firestore error');
        try {
          const response = await api.get(getFirebaseFunctionUrl('getEmployees'));
          const employees = response.data.data || response.data.employees || [];
          saveCache(cacheKey, employees);
          return employees;
        } catch (fallbackError) {
          console.error('Fallback Firebase Function also failed:', fallbackError);
        }
        
        // Return cached data if available, even if stale
        if (cached) {
          console.warn('Using stale cache due to all methods failing');
          return cached.data;
        }
        
        return [];
      }
    });
  },

  async getEmployee(id: string): Promise<Employee | null> {
    const cacheKey = `${EMPLOYEE_CACHE_KEY}_employee_${id}`;
    const cached = loadCache<Employee>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      // Use Firebase Function for individual employee retrieval
      const response = await api.get(getFirebaseFunctionUrl('getEmployeeById'), {
        params: { id }
      });
      
      if (response.data.success && response.data.data) {
        const employee = response.data.data;
        // Save to cache
        saveCache(cacheKey, employee);
        return employee;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching employee by ID:', error);
      
      // Return cached data if available, even if stale
      if (cached) {
        console.warn(`Using stale cache for employee ${id}`);
        return cached.data;
      }
      
      // Fallback to local search if Firebase Function fails
      try {
        console.warn('Falling back to local employee search');
        const employees = await this.getAllEmployees();
        const employee = employees.find(emp => emp.id === id);
        if (employee) {
          // Save to cache for future use
          saveCache(cacheKey, employee);
        }
        return employee || null;
      } catch (fallbackError) {
        console.error('Fallback employee search also failed:', fallbackError);
        return null;
      }
    }
  },

  async searchEmployees(query: string, filters?: EmployeeFilters): Promise<SearchResult> {
    // For now, use getAllEmployees and filter locally
    try {
      const allEmployees = await this.getAllEmployees();
      let filtered = allEmployees;

      // Apply search query
      if (query.trim()) {
        const searchQuery = query.toLowerCase();
        filtered = filtered.filter(employee =>
          employee.firstName?.toLowerCase().includes(searchQuery) ||
          employee.lastName?.toLowerCase().includes(searchQuery) ||
          employee.email?.toLowerCase().includes(searchQuery) ||
          employee.department?.toLowerCase().includes(searchQuery)
        );
      }

      // Apply filters
      if (filters?.department) {
        filtered = filtered.filter(emp => emp.department === filters.department);
      }
      if (filters?.country) {
        filtered = filtered.filter(emp => emp.country === filters.country);
      }
      if (filters?.status) {
        filtered = filtered.filter(emp => emp.status === filters.status);
      }

      return {
        employees: filtered,
        totalCount: filtered.length,
        hasMore: false,
        filters: filters || { search: '', department: '', country: '', status: '', sortBy: 'firstName', sortOrder: 'asc' }
      };
    } catch (error) {
      console.error('Error searching employees:', error);
      return { employees: [], totalCount: 0, hasMore: false, filters: filters || { search: '', department: '', country: '', status: '', sortBy: 'firstName', sortOrder: 'asc' } };
    }
  },

  async getEmployeesByDepartment(department: string): Promise<Employee[]> {
    try {
      const employees = await this.getAllEmployees();
      return employees.filter(emp => emp.department === department);
    } catch (error) {
      console.error('Error fetching employees by department:', error);
      return [];
    }
  },

  async getEmployeesByLocation(location: string): Promise<Employee[]> {
    try {
      const employees = await this.getAllEmployees();
      return employees.filter(emp => emp.country === location);
    } catch (error) {
      console.error('Error fetching employees by location:', error);
      return [];
    }
  },

  async getDashboardMetrics(): Promise<EmployeeDashboardStats> {
    const cacheKey = `${EMPLOYEE_CACHE_KEY}_dashboard_metrics`;
    const cached = loadCache<EmployeeDashboardStats>(cacheKey);
    const now = Date.now();

    // Return cached data if fresh
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    try {
      const response = await api.get(getFirebaseFunctionUrl('getEmployeeDashboardStats'));
      const statsData = response.data.data || response.data || {};
      
      // Transform the Firebase function response to match our expected format
      const metrics = {
        totalEmployees: statsData.totalEmployees || 0,
        totalDepartments: statsData.totalDepartments || 0,
        totalCountries: statsData.totalCountries || 0,
        totalCities: statsData.totalCities || 0,
        activeEmployees: statsData.totalEmployees || 0, // Assuming all are active for now
        newHiresThisMonth: statsData.recentHires || 0,
        upcomingBirthdays: [],
        upcomingAnniversaries: [],
        departmentBreakdown: Object.entries(statsData.departmentCounts || {}).map(([department, count]) => ({
          department,
          count: count as number,
          percentage: ((count as number) / (statsData.totalEmployees || 1)) * 100
        })),
        countryBreakdown: Object.entries(statsData.countryCounts || {}).map(([country, count]) => ({
          country,
          count: count as number,
          percentage: ((count as number) / (statsData.totalEmployees || 1)) * 100
        })),
        lastUpdated: new Date().toISOString()
      };
      
      // Save to cache
      saveCache(cacheKey, metrics);
      return metrics;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      
      // Return cached data if available
      if (cached) {
        console.warn('Using stale cache for dashboard metrics');
        return cached.data;
      }
      
      return {
        totalEmployees: 0,
        totalDepartments: 0,
        totalCountries: 0,
        totalCities: 0,
        activeEmployees: 0,
        newHiresThisMonth: 0,
        upcomingBirthdays: [],
        upcomingAnniversaries: [],
        departmentBreakdown: [],
        countryBreakdown: [],
        lastUpdated: new Date().toISOString()
      };
    }
  },

  async getDepartmentStats(): Promise<DepartmentStats[]> {
    try {
      const employees = await this.getAllEmployees();
      const departmentCounts: Record<string, number> = {};
      
      employees.forEach(emp => {
        if (emp.department) {
          departmentCounts[emp.department] = (departmentCounts[emp.department] || 0) + 1;
        }
      });

      return Object.entries(departmentCounts).map(([department, count]) => ({
        department,
        count,
        percentage: (count / employees.length) * 100
      }));
    } catch (error) {
      console.error('Error calculating department stats:', error);
      return [];
    }
  },

  async getLocationStats(): Promise<CountryStats[]> {
    try {
      const employees = await this.getAllEmployees();
      const countryCounts: Record<string, number> = {};
      
      employees.forEach(emp => {
        if (emp.country) {
          countryCounts[emp.country] = (countryCounts[emp.country] || 0) + 1;
        }
      });

      return Object.entries(countryCounts).map(([country, count]) => ({
        country,
        count,
        percentage: (count / employees.length) * 100
      }));
    } catch (error) {
      console.error('Error calculating location stats:', error);
      return [];
    }
  },

  async updateEmployee(employee: Employee): Promise<void> {
    throw new Error('Employee update functionality not yet implemented');
  },

  async addEmployee(employee: Omit<Employee, 'id'>): Promise<string> {
    throw new Error('Employee add functionality not yet implemented');
  },

  async deleteEmployee(id: string): Promise<void> {
    throw new Error('Employee delete functionality not yet implemented');
  },

  async uploadEmployeePhoto(id: string, photoFile: File): Promise<string> {
    throw new Error('Photo upload functionality not yet implemented');
  },

  async deleteEmployeePhoto(id: string): Promise<void> {
    throw new Error('Photo delete functionality not yet implemented');
  },

  async getFilterOptions(): Promise<{
    departments: string[];
    locations: string[];
    jobTitles: string[];
    skills: string[];
  }> {
    try {
      // Generate filter options from all employees data
      const employees = await this.getAllEmployees();
      
      const departments = new Set<string>();
      const locations = new Set<string>();
      const jobTitles = new Set<string>();
      const skills = new Set<string>();

      employees.forEach(employee => {
        if (employee.department) departments.add(employee.department);
        if (employee.country) locations.add(employee.country);
        if (employee.position) jobTitles.add(employee.position);
        if (employee.skills) {
          employee.skills.forEach(skill => skills.add(skill));
        }
      });

      return {
        departments: Array.from(departments).sort(),
        locations: Array.from(locations).sort(),
        jobTitles: Array.from(jobTitles).sort(),
        skills: Array.from(skills).sort()
      };
    } catch (error) {
      console.error('Error generating filter options:', error);
      return {
        departments: [],
        locations: [],
        jobTitles: [],
        skills: []
      };
    }
  },

  // Admin functions
  async getAdmins(): Promise<EmployeeDirectoryAdmin[]> {
    try {
      // Always fetch fresh data for admins to avoid cache issues
      const response = await api.get(getFirebaseFunctionUrl('getEmployeeDirectoryAdmins'));
      const admins = response.data.data || [];
      console.log('Fetched admins from API:', admins);
      return admins;
    } catch (error) {
      console.error('Error fetching admins:', error);
      return [];
    }
  },

  async addAdmin(email: string, firstName?: string, lastName?: string): Promise<void> {
    try {
      await api.post(getFirebaseFunctionUrl('addEmployeeDirectoryAdmin'), {
        email,
        firstName,
        lastName
      });
      
      // Clear ALL admin-related cache after adding
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('admin') || key.includes('Admin')) {
          localStorage.removeItem(key);
        }
      });
      
      // Also clear the main employee directory cache to force refresh
      clearEmployeeDirectoryCache();
    } catch (error) {
      console.error('Error adding admin:', error);
      throw error;
    }
  },

  async removeAdmin(email: string): Promise<void> {
    try {
      await api.delete(getFirebaseFunctionUrl('removeEmployeeDirectoryAdmin'), {
        params: { email }
      });
      
      // Clear ALL admin-related cache after removing
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('admin') || key.includes('Admin')) {
          localStorage.removeItem(key);
        }
      });
      
      // Also clear the main employee directory cache to force refresh
      clearEmployeeDirectoryCache();
    } catch (error) {
      console.error('Error removing admin:', error);
      throw error;
    }
  },

  async checkUserAuth(email: string): Promise<{ isAuthorized: boolean; isAdmin: boolean }> {
    try {
      const response = await api.get(getFirebaseFunctionUrl('checkEmployeeDirectoryAuth'), {
        params: { email }
      });
      
      return {
        isAuthorized: response.data.isAuthorized || false,
        isAdmin: response.data.isAdmin || false
      };
    } catch (error) {
      console.error('Error checking user auth:', error);
      return { isAuthorized: false, isAdmin: false };
    }
  },

  async manualSync(): Promise<{ success: boolean; message: string; stats?: any }> {
    try {
      const manualSyncFunction = httpsCallable(functions, 'employeeDirectoryManualSync');
      const result = await manualSyncFunction();
      
      // Clear all cache after successful sync
      clearEmployeeDirectoryCache();
      
      return result.data as { success: boolean; message: string; stats?: any };
    } catch (error) {
      console.error('Error during manual sync:', error);
      throw error;
    }
  }
};